/**
 * Failure Logs Page — 실패 로그
 * 접근: 제품 관리자(productAdmin) + 테넌트 관리자(tenantAdmin)
 *
 * URL: /admin/logs/failures
 */

import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Badge, Pagination, SearchInput, Select, Tabs, useToast } from '../components/ui';
import type { TabItem } from '../components/ui';
import { logsApi } from '../api/logs';
import { authApi, tenantApi } from '../api';
import { workflowApi } from '../api/workflows';
import type { WorkflowRunLog } from '../api/workflows';
import { isProductAdminRole } from '../utils/role';
import CloseIcon from '../assets/icons/common/close.svg?react';
import CheckIcon from '../assets/icons/common/check.svg?react';
import SkipIcon from '../assets/icons/common/skip.svg?react';
import ExternalLinkIcon from '../assets/icons/common/external-link.svg?react';
import '../styles/adminPage.css';

// ── 필터 탭 ───────────────────────────────────────────────────────────────────

type TriggerFilter = 'all' | 'schedule' | 'manual';

const TRIGGER_TABS: TabItem<TriggerFilter>[] = [
  { key: 'all',      label: '전체' },
  { key: 'schedule', label: '스케줄' },
  { key: 'manual',   label: '수동' },
];

const PAGE_SIZE = 15;

// ── 날짜 포맷 ─────────────────────────────────────────────────────────────────

function fmtDatetime(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' });
  const time = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `${date} ${time}`;
}

function fmtDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function FailureLogsPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<WorkflowRunLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [triggerFilter, setTriggerFilter] = useState<TriggerFilter>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [tenantFilter, setTenantFilter] = useState('');
  const [tenants, setTenants] = useState<{ id: string; name: string }[]>([]);
  const [tenantNameMap, setTenantNameMap] = useState<Map<string, string>>(new Map());
  // 현존 워크플로우 ID — null이면 아직 로딩 중 (false positive 방지)
  const [existingWorkflowIds, setExistingWorkflowIds] = useState<Set<string> | null>(null);

  const currentUser = authApi.getUser();
  const isProductAdmin = isProductAdminRole(currentUser?.role);

  useEffect(() => {
    if (isProductAdmin) tenantApi.list().then(ts => {
      setTenants(ts);
      setTenantNameMap(new Map(ts.map(t => [t.id, t.name])));
    }).catch(() => {});
  }, [isProductAdmin]);

  // 현존 워크플로우 ID 로드 — 삭제 여부 판단용 (조용히 실패)
  useEffect(() => {
    workflowApi.list({ limit: 500 })
      .then(wfs => setExistingWorkflowIds(new Set(wfs.map(w => w.id))))
      .catch(() => setExistingWorkflowIds(new Set()));
  }, []);

  const isWorkflowDeleted = (workflowId: string): boolean =>
    existingWorkflowIds !== null && !existingWorkflowIds.has(workflowId);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await logsApi.getFailures({ limit: 200, tenantId: tenantFilter || undefined });
      setLogs(data);
    } catch {
      showToast('error', '실패 로그를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showToast, tenantFilter]);

  useEffect(() => { load(); }, [load]);
  // tenantFilter 변경 시 load 재호출은 load가 tenantFilter에 의존하므로 자동 처리

  const filtered = logs.filter((l) => {
    if (triggerFilter !== 'all' && l.triggeredBy !== triggerFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      l.workflowName?.toLowerCase().includes(q) ||
      l.errorMessage?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged      = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // 검색 · 탭이 바뀌면 첫 페이지로
  useEffect(() => { setCurrentPage(1); }, [search, triggerFilter]);

  const toggleExpand = (logId: string, hasResults: boolean) => {
    if (!hasResults) return; // nodeResults 없으면 펼침 안 함
    setExpanded((prev) => (prev === logId ? null : logId));
  };

  // 검색바 (사용자 관리와 동일한 구조)
  const searchBar = (
    <div className="users-search-bar">
      <SearchInput
        placeholder="워크플로우명, 오류 내용으로 검색..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onClear={() => setSearch('')}
      />
      <button
        className="users-search-refresh"
        onClick={() => load(true)}
        disabled={refreshing}
        title="새로고침"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
          <path d="M21 3v5h-5"/>
        </svg>
      </button>
    </div>
  );

  return (
    <>
      <Topbar title="실패 로그" description="실패한 워크플로우 실행 이력을 조회합니다" />

      <div className="page-content">
        {/* 탭 + 검색바 줄 */}
        <div className="tabs-row">
          <Tabs items={TRIGGER_TABS} active={triggerFilter} onChange={setTriggerFilter} />
          <div className="tabs-row-aside admin-panel-actions users-tabs-aside">
            {isProductAdmin && (
              <Select value={tenantFilter} onChange={e => { setTenantFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">전체 테넌트</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            )}
            {searchBar}
          </div>
        </div>

        <div className="admin-section">
          {loading ? (
            <div className="loading">로딩 중...</div>
          ) : filtered.length === 0 ? (
            <div className="logs-empty">
              <p className="logs-empty-title">
                {logs.length === 0 ? '실패 로그가 없습니다' : '검색 결과가 없습니다'}
              </p>
              {logs.length === 0 && (
                <p className="logs-empty-desc">
                  배포된 워크플로우가 실패하면 여기에 기록됩니다.
                </p>
              )}
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>워크플로우</th>
                    {isProductAdmin && !tenantFilter && <th>테넌트</th>}
                    <th>트리거</th>
                    <th>시작 시각</th>
                    <th>소요 시간</th>
                    <th>오류 내용</th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((log) => {
                    const hasResults = !!(log.nodeResults && log.nodeResults.length > 0);
                    return (
                    <Fragment key={log.logId}>
                      <tr
                        className={`logs-failure-row${expanded === log.logId ? ' expanded' : ''}`}
                        onClick={() => toggleExpand(log.logId, hasResults)}
                        style={{ cursor: hasResults ? 'pointer' : 'default' }}
                      >
                        <td className="left">
                          <span className={`logs-failure-name${isWorkflowDeleted(log.workflowId) ? ' logs-failure-name--deleted' : ''}`}>
                            {log.workflowName}
                          </span>
                          {isWorkflowDeleted(log.workflowId) && (
                            <span className="logs-deleted-badge">삭제됨</span>
                          )}
                        </td>
                        {isProductAdmin && !tenantFilter && (
                          <td className="left mute" style={{ fontSize: 12 }}>
                            {log.metadata?.tenantId ? (tenantNameMap.get(log.metadata.tenantId) || log.metadata.tenantId) : '-'}
                          </td>
                        )}
                        <td>
                          <Badge color="gray" shape="tag">
                            {log.triggeredBy === 'schedule' ? '스케줄' : '수동'}
                          </Badge>
                        </td>
                        <td className="left mono mute" style={{ whiteSpace: 'nowrap' }}>
                          {fmtDatetime(log.startedAt)}
                        </td>
                        <td className="mute mono">
                          {log.durationMs != null ? fmtDuration(log.durationMs) : '-'}
                        </td>
                        <td className="left">
                          <span className="logs-error-msg" title={log.errorMessage}>
                            {log.errorMessage
                              ? log.errorMessage.length > 60
                                ? log.errorMessage.slice(0, 60) + '…'
                                : log.errorMessage
                              : '-'}
                          </span>
                        </td>
                        <td>
                          <div className="user-actions">
                            <button
                              className="icon-action-btn"
                              title={isWorkflowDeleted(log.workflowId) ? '삭제된 워크플로우' : '워크플로우로 이동'}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (log.workflowId && !isWorkflowDeleted(log.workflowId)) navigate(`/workflows/${log.workflowId}`);
                              }}
                              disabled={!log.workflowId || isWorkflowDeleted(log.workflowId)}
                            >
                              <ExternalLinkIcon />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* 노드별 실행 결과 상세 */}
                      {expanded === log.logId && log.nodeResults && log.nodeResults.length > 0 && (
                        <tr className="logs-detail-row">
                          <td colSpan={isProductAdmin && !tenantFilter ? 7 : 6}>
                            <div className="logs-node-results">
                              <div className="logs-node-table-wrap">
                                <table className="logs-node-table">
                                  <thead>
                                    <tr>
                                      <th style={{ width: 28 }}></th>
                                      <th>노드</th>
                                      <th>메시지</th>
                                      <th style={{ width: 60 }}>소요</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {log.nodeResults.map((nr, i) => {
                                      const StatusIcon =
                                        nr.status === 'success' ? CheckIcon :
                                        nr.status === 'failed'  ? CloseIcon : SkipIcon;
                                      return (
                                        <tr key={i} className={`logs-node-row--${nr.status}`}>
                                          <td><StatusIcon className="logs-node-status-icon" /></td>
                                          <td className="mono">{nr.nodeType}</td>
                                          <td>{nr.message || <span className="mute">-</span>}</td>
                                          <td className="mono">{nr.durationMs != null ? fmtDuration(nr.durationMs) : '-'}</td>
                                        </tr>
                                      );
                                    })}
                                    {log.errorMessage && (
                                      <tr className="logs-node-row--error-summary">
                                        <td colSpan={4}>전체 오류: {log.errorMessage}</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 페이지네이션 (공통) */}
        {!loading && totalPages > 1 && (
          <Pagination
            current={currentPage}
            total={totalPages}
            onChange={setCurrentPage}
            info={<><b>{(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)}</b> / {filtered.length}건</>}
          />
        )}
      </div>
    </>
  );
}

export default FailureLogsPage;
